/**
 * Permission codes for the store-settings domains.
 *
 * Mirrors the BFF permission model (see the planning doc). Consume with
 * `usePermissions().hasPermission(...)` from `@/shared/permissions` to gate future
 * settings screens and actions. Codes are dot-namespaced under `settings.<domain>.*`
 * and honour the `settings.*` wildcard supported by the permission context.
 */
import type { PermissionCode } from '@/shared/permissions';

export const STORE_SETTINGS_PERMISSIONS = {
  general: {
    view: 'settings.general.view',
    manage: 'settings.general.manage',
  },
  delivery: {
    view: 'settings.delivery.view',
    manage: 'settings.delivery.manage',
    credentials: 'settings.delivery.credentials.manage',
  },
  payments: {
    view: 'settings.payments.view',
    manage: 'settings.payments.manage',
    credentials: 'settings.payments.credentials.manage',
  },
  checkout: {
    view: 'settings.checkout.view',
    manage: 'settings.checkout.manage',
  },
  orders: {
    view: 'settings.orders.view',
    manage: 'settings.orders.manage',
    lifecycle: 'settings.orders.lifecycle.manage',
  },
  pricing: {
    view: 'settings.pricing.view',
    manage: 'settings.pricing.manage',
    credentials: 'settings.pricing.credentials.manage',
  },
  catalog: {
    view: 'settings.catalog.view',
    manage: 'settings.catalog.manage',
  },
  languages: {
    view: 'catalog.structure.view',
    manage: 'catalog.structure.manage',
  },
  customers: {
    view: 'settings.customers.view',
    manage: 'settings.customers.manage',
  },
  notifications: {
    view: 'settings.notifications.view',
    manage: 'settings.notifications.manage',
    credentials: 'settings.notifications.credentials.manage',
  },
  content: {
    view: 'settings.content.view',
    manage: 'settings.content.manage',
  },
  engagement: {
    view: 'settings.engagement.view',
    manage: 'settings.engagement.manage',
  },
  telegram: {
    view: 'settings.telegram.view',
    manage: 'settings.telegram.manage',
    credentials: 'settings.telegram.credentials.manage',
  },
  max: {
    view: 'settings.max.view',
    manage: 'settings.max.manage',
    credentials: 'settings.max.credentials.manage',
  },
  web: {
    view: 'settings.web.view',
    manage: 'settings.web.manage',
  },
  ozon: {
    view: 'settings.ozon.view',
    manage: 'settings.ozon.manage',
  },
  wildberries: {
    view: 'settings.wildberries.view',
    manage: 'settings.wildberries.manage',
  },
  'yandex-market': {
    view: 'settings.yandex-market.view',
    manage: 'settings.yandex-market.manage',
  },
  lamoda: {
    view: 'settings.lamoda.view',
    manage: 'settings.lamoda.manage',
  },
  // ── Platform services added in product-catalog/docs/obzr-ecom-platform.md ──────
  tenant: {
    view: 'settings.tenant.view',
    manage: 'settings.tenant.manage',
  },
  store: {
    view: 'settings.store.view',
    manage: 'settings.store.manage',
  },
  address: {
    view: 'settings.address.view',
    manage: 'settings.address.manage',
    credentials: 'settings.address.credentials.manage',
  },
  inventory: {
    view: 'settings.inventory.view',
    manage: 'settings.inventory.manage',
  },
  media: {
    view: 'settings.media.view',
    manage: 'settings.media.manage',
  },
  'ai-agents': {
    view: 'settings.ai-agents.view',
    manage: 'settings.ai-agents.manage',
    credentials: 'settings.ai-agents.credentials.manage',
  },
  // The chatbot behaviour domain (plan-conversation-001): general → per-channel settings for the
  // customer-conversation hub, fronted by web-merchant-portal-bff's `/api/v1/store/settings/chatbot`.
  // Minted here in WP-MP.0 ahead of its screen (WP-MP.4) so the permission model, mock session and
  // route-guard registry can be built against real codes now — see `SETTINGS_CODES_WITHOUT_PAGES` in
  // `shared/navigation/appSections.ts` for why this domain is temporarily excluded from the Settings
  // rail's own reachability check until the page ships.
  chatbot: {
    view: 'settings.chatbot.view',
    manage: 'settings.chatbot.manage',
  },
  // The chatbot templates & FAQ editor (plan-conversation-001 WP-MP.2, adr-merchant-portal-019 D3):
  // deliberately reuses the `content.*` domain rather than `settings.chatbot.*` — the texts are
  // tenant content that becomes customer-visible (adr-auth-006 D5), same reasoning as `languages`
  // reusing `catalog.structure.*`. Writes additionally require the `content`/`publish` capability
  // (`content.feature.publish`, checked directly via `usePermissions()` in the page — there is no
  // "credentials" tier here, so `useDomainPermissions` has nothing to project it onto).
  'chatbot-templates': {
    view: 'content.view',
    manage: 'content.manage',
  },
  basket: {
    view: 'settings.basket.view',
    manage: 'settings.basket.manage',
  },
  // The unified sales-channel & touchpoint registry (adr-tenant-001, plan-tenant-001 Phase 5 —
  // → web-tenant-settings-service via the BFF's `/api/v1/sales-channels`): channel identity CRUD
  // + touchpoint kill-switches. Mirrors the BFF's "channels" settings domain in
  // RolePermissionMapper. No credentials tier — the registry stores identity/structure only.
  channels: {
    view: 'settings.channels.view',
    manage: 'settings.channels.manage',
  },
  'retail-evotor': {
    view: 'settings.retail-evotor.view',
    manage: 'settings.retail-evotor.manage',
    credentials: 'settings.retail-evotor.credentials.manage',
  },
  'erp-posiflora': {
    view: 'settings.erp-posiflora.view',
    manage: 'settings.erp-posiflora.manage',
    credentials: 'settings.erp-posiflora.credentials.manage',
  },
  // Evotor process-sync integration (→ evotor-integration-service): tenant-intent toggles for the
  // platform-wide sync pipeline's steps (product/category export, store/employee/device sync,
  // receipt ingestion). Bare `integrations.evotor.*` codes — NOT the `settings.<domain>.*` pattern
  // every other domain above uses. Confirmed directly against the shipped BFF contract (the
  // `web-merchant-portal-bff` team's parallel work package): this resource lives at a genuinely
  // different route family (`/api/v1/integrations/evotor/**`, not `/api/v1/store/settings/**`),
  // and its RBAC mapping mints its own `integrations.evotor.view`/`.manage` codes rather than
  // extending the `store-settings` app's generic `RolePermissionMapper` DOMAINS loop — so it does
  // NOT fall under the pre-existing `settings.*` wildcard (see `DEFAULT_PERMISSIONS`/`MOCK_ME` for
  // the matching mock-mode grants). No credentials sub-tier (this domain has no credentialed
  // sub-resource — Evotor's API token is owned by the separate `retail-evotor` domain above).
  'evotor-integration': {
    view: 'integrations.evotor.view',
    manage: 'integrations.evotor.manage',
  },
} as const satisfies Record<string, Record<string, PermissionCode>>;

/** Settings domain keys, e.g. `'delivery'`. */
export type StoreSettingsDomain = keyof typeof STORE_SETTINGS_PERMISSIONS;

/** Flat list of every settings permission code (useful for mocks / tests). */
export const ALL_STORE_SETTINGS_PERMISSIONS: PermissionCode[] = Object.values(
  STORE_SETTINGS_PERMISSIONS,
).flatMap((domain) => Object.values(domain));
